'use client';

import Icon from '@/components/ui/AppIcon';

interface ThirdPartySectionProps {
  isExpanded: boolean;
  onToggle: () => void;
}

export default function ThirdPartySection({ isExpanded, onToggle }: ThirdPartySectionProps) {
  const thirdParties = [
    {
      category: 'Payment Processors',
      providers: ['Stripe', 'PayPal', 'Razorpay'],
      purpose: 'Secure payment processing and transaction management',
      dataShared: 'Payment details, billing address, transaction amount',
      safeguards: 'PCI-DSS Level 1 certified, encrypted transmission, tokenization'
    },
    {
      category: 'Booking Platforms',
      providers: ['Booking.com', 'Airbnb', 'Expedia'],
      purpose: 'Channel management and reservation synchronization',
      dataShared: 'Guest name, contact details, booking dates, preferences',
      safeguards: 'Data processing agreements, GDPR compliance, limited access'
    },
    {
      category: 'Analytics Services',
      providers: ['Google Analytics', 'Hotjar', 'Mixpanel'],
      purpose: 'Website performance monitoring and user behavior analysis',
      dataShared: 'Anonymized usage data, device information, IP addresses',
      safeguards: 'IP anonymization, data retention limits, opt-out mechanisms'
    },
    {
      category: 'Communication Tools',
      providers: ['SendGrid', 'Twilio', 'WhatsApp Business'],
      purpose: 'Email delivery, SMS notifications, customer support',
      dataShared: 'Email address, phone number, message content',
      safeguards: 'Encrypted transmission, data processing agreements, GDPR compliance'
    }
  ];

  return (
    <section id="third-party" className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-primary/10 px-4 py-2 rounded-full mb-4">
              <Icon name="LinkIcon" size={20} className="text-primary" />
              <span className="text-primary text-sm font-medium">Third-Party Integrations</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-display font-bold text-text-primary mb-4">
              Third-Party Services
            </h2>
            <p className="text-lg text-text-secondary">
              We work with trusted partners to provide seamless services. All third-party integrations are carefully vetted for security and privacy compliance.
            </p>
          </div>

          <div className="space-y-6">
            {thirdParties.map((party, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow-card border border-border overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-2">
                        {party.category}
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {party.providers.map((provider, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full"
                          >
                            {provider}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm font-medium text-text-primary mb-1">Purpose:</p>
                      <p className="text-sm text-text-secondary">{party.purpose}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-text-primary mb-1">Data Shared:</p>
                      <p className="text-sm text-text-secondary">{party.dataShared}</p>
                    </div>
                    <button
                      onClick={onToggle}
                      className="text-primary hover:text-primary/80 text-sm font-medium flex items-center space-x-1 transition-colors"
                    >
                      <span>{isExpanded ? 'Hide Safeguards' : 'View Safeguards'}</span>
                      <Icon 
                        name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'} 
                        size={14} 
                        className="text-current" 
                      />
                    </button>
                    {isExpanded && (
                      <div className="mt-3 p-4 bg-green-50 rounded-lg border border-green-200 animate-scale-in">
                        <div className="flex items-start space-x-2">
                          <Icon name="ShieldCheckIcon" size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm font-medium text-green-900 mb-1">Privacy Safeguards:</p>
                            <p className="text-sm text-green-800">{party.safeguards}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 p-6 bg-accent/10 rounded-xl border border-accent/20">
            <div className="flex items-start space-x-3">
              <Icon name="ExclamationTriangleIcon" size={24} className="text-accent flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-semibold text-text-primary mb-2">International Data Transfers</h4>
                <p className="text-text-secondary text-sm mb-3">
                  Some of our third-party service providers are located outside India. We ensure adequate protection through:
                </p>
                <ul className="space-y-1 text-text-secondary text-sm">
                  <li className="flex items-start space-x-2">
                    <Icon name="CheckIcon" size={14} className="text-accent mt-1 flex-shrink-0" />
                    <span>Standard Contractual Clauses (SCCs) approved by regulatory authorities</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="CheckIcon" size={14} className="text-accent mt-1 flex-shrink-0" />
                    <span>Adequacy decisions recognizing equivalent data protection standards</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="CheckIcon" size={14} className="text-accent mt-1 flex-shrink-0" />
                    <span>Binding Corporate Rules for multinational service providers</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}